import PropTypes from 'prop-types';
import React, { useState, useEffect, useCallback } from 'react';
import { Alert, Button, Form, Icon, Input, Modal, Spin, Progress } from 'antd';
import { subscribe } from 'alfa';

import JWK from '../../helpers/jwk';
import Security from '../../helpers/security';


const sleep = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds));

const SignWindow = (
  { form: { getFieldDecorator, validateFields, resetFields }, payload, user, multiple, onCancel, onOk, visible },
) => {
  const [error, setError] = useState('');
  const [isSigning, setIsSigning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [data, setData] = useState([]);
  const [signedData, setSignedData] = useState([]);
  const [keySign, setKeySign] = useState(null);

  const handleOnCancel = () => {
    setIsSigning(false);
    setProgress(0);
    setKeySign(null);
    setData([]);
    setSignedData([]);
    if (onCancel) { onCancel(); }
  };

  const handleOnOk = (event) => {
    event.preventDefault();
    if (isSigning) { return; }

    setError('');
    setProgress(0);
    setIsSigning(true);
  };

  const dataSigner = useCallback((givenData, keyToSign) => {
    const refreshEvery = parseInt(givenData.length / 100, 10) || 1;
    const response = givenData.map(async (row, index) => {
      const signature = JWK.sign(row, keyToSign);
      if (index % refreshEvery === 0) {
        await sleep(1);
        setProgress(100 * (index / givenData.length));
      }
      return { ...row, signature };
    });
    return Promise.all(response);
  }, []);

  useEffect(() => {
    if (!signedData.length) { return; }
    setIsSigning(false);
    onOk((multiple !== true) ? signedData[0] : signedData);
    setKeySign(null);
    setData([]);
    setSignedData([]);
    resetFields();
  }, [multiple, onOk, signedData, resetFields]);

  useEffect(() => {
    if (!data.length || !keySign) { return; }
    const signData = async () => {
      const response = await dataSigner(data, keySign);
      setSignedData(response);
    };
    signData();
  }, [data, dataSigner, keySign, multiple, onOk]);

  const startSigning = useCallback(async (callbackError, values) => {
    const { password } = values;

    if (callbackError) {
      setIsSigning(false);
      return;
    }
    let encrypted;
    try {
      encrypted = JSON.parse(Security.getKey(user.getData()));
    } catch (parseError) {
      setError('Unknown error');
      setIsSigning(false);
      console.error('ERROR: BalanceDiplayer: AccountRepo.updateBalance()', parseError); // eslint-disable-line no-console
      return;
    }
    try {
      const newKey = await JWK.decrypt(encrypted, password);
      setKeySign(newKey);
    } catch (decryptError) {
      setError('Failed to decrypt your key, please check your password.');
      setIsSigning(false);
      return;
    }

    let newData = payload;
    if (multiple !== true) {
      newData = [newData];
    }
    setData(newData);
  }, [multiple, payload, user]);

  useEffect(() => {
    if (isSigning) { validateFields(startSigning); }
  }, [isSigning, startSigning, validateFields]);

  return (
    <Modal
      title="Signing operation"
      centered
      visible={visible}
      onOk={handleOnOk}
      onCancel={handleOnCancel}
      okButtonProps={{ disabled: isSigning }}
    >
      <p>In order to sign this transaction with your stored key we&apos;ll need to ask for your password.</p>
      <Alert
        type="warning"
        message={'We never store your key\'s password and this password will never leave your browser.'}
        showIcon
      />

      {(error)
        ? (<Alert type="error" showIcon message={error} style={{ marginTop: '20px' }} />)
        : ('')}

      <Spin tip="Signing transactions ..." spinning={isSigning}>
        <Progress percent={progress} />
        <Form onSubmit={handleOnOk}>
          <Form.Item>
            {getFieldDecorator('password', {
              rules: [{ required: true, message: 'Please input your Password!' }],
            })(
              <Input.Password
                className="margin-top-1"
                prefix={<Icon type="lock" />}
                placeholder="Password"
              />,
            )}
          </Form.Item>
          <Button type="ghost" htmlType="submit" className="hidden" tabIndex={-1} />
        </Form>
      </Spin>
    </Modal>
  );
};

SignWindow.propTypes = {
  multiple: PropTypes.bool,
  onCancel: PropTypes.func,
  onOk: PropTypes.func,
  payload: PropTypes.any,
  user: PropTypes.shape({
    getData: PropTypes.func,
  }),
  form: PropTypes.shape({
    validateFields: PropTypes.func,
    getFieldDecorator: PropTypes.func,
    resetFields: PropTypes.func,
  }),
  visible: PropTypes.any,
};

export { SignWindow };
export default subscribe(Form.create({ name: 'signing-window' })(SignWindow), ['user']);
